import {
  contactLanguageSchema,
  questionnaireQuestionId,
  validatedPropertyInterestDataSchema,
  type PropertyInterestData,
  type QuestionnaireQuestionId,
  type RespondedQuestionnaireAnswers,
} from '@aikoa/common'
import z from 'zod'

export const feedbackAnswersSchema = z
  .partialRecord(questionnaireQuestionId, z.union([z.number(), z.string()]))

export const feedbackFlowDataSchema = z.object({
  feedbackAnswers: feedbackAnswersSchema,
  propertyInterest: validatedPropertyInterestDataSchema
    .pick({
      filters: true,
      locations: true,
    })
    .optional(),
  interestDescription: validatedPropertyInterestDataSchema
    .pick({
      description: true,
    })
    .optional(),
  consentAuth: z
    .object({
      requiresConsentAuth: z.boolean(),
    })
    .optional(),
  contactDetails: z
    .object({
      name: z.string().optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
      language: contactLanguageSchema.optional(),
    })
    .optional(),
})

export type FeedbackFlowData = z.infer<typeof feedbackFlowDataSchema>

export function feedbackFlowDataToQuestionnaireAnswers(
  data: FeedbackFlowData['feedbackAnswers'] | null | undefined
): RespondedQuestionnaireAnswers | null {
  if (!data || !Object.keys(data).length) {
    return null
  }

  return Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .map(([id, value]) => ({
    id: id as QuestionnaireQuestionId,
    value,
  }))
}

export function buildInterestPayload(
  data: Partial<FeedbackFlowData>
): PropertyInterestData | undefined {
  const answers = feedbackFlowDataToQuestionnaireAnswers(data.feedbackAnswers)

  if (!answers || answers.length === 0) {
    return undefined
  }

  const interest = data.propertyInterest
  const interestDescription = data.interestDescription

  return {
    answers,
    filters: interest?.filters ?? {},
    locations: interest?.locations ?? [],
    description: (interestDescription?.description ?? null) as string | null,
  }
}

export type ContactForConsent = {
  id: string
  name: string
  email: string | null
  phone: string | null
  language: string
  disabled_at: string | null
  disabled_reason: 'RESERVED' | 'INACTIVE' | 'BLOCKED' | null
}
